<template>
  <div class="check-in-card-wrapper">
    <div class="d-flex justify-content-between" v-if="isCheckinCollapsed">
      <div
        class="text-secondary placeholder text-medium-1 flex-grow-1"
        @click="isCheckinCollapsed = false"
      >
        Add a check-in
      </div>
      <subscribers :org-id="orgId" :entity-type="ENTITY_TYPE.KEY_RESULT" :entity="kr" />
    </div>
    <div class="no-gutters check-in-card" tabindex="-1" @keydown="onCardKeyDown" v-else>
      <div class="d-flex mb-4" v-if="isLinkedWithSpreadsheet">
        <i class="google-sheet-icon mr-2"></i>
        <span>
          Updated via Google sheets
          <a
            v-if="sheetInfo.sheetTitle && sheetInfo.cell"
            class="small text-secondary"
            :href="sheetInfo.spreadsheetUrl"
            target="_blank"
          >
            <span v-if="sheetInfo.spreadsheetTitle">{{ sheetInfo.spreadsheetTitle }}, </span
            ><span>{{ sheetInfo.sheetTitle }}!{{ sheetInfo.cell }}</span>
          </a>
        </span>
      </div>
      <div class="row no-gutters flex-nowrap checkin-value-wrapper">
        <div class="bool-handler" v-if="kr.measureBy === KR_MEASURING_UNIT.BOOLEAN">
          <label class="text-nowrap text-medium-1">
            Currently:
            <span class="status">{{ kr.current === 1 ? 'Completed' : 'Open' }}</span></label
          >
          <div>
            <b-form-checkbox
              class="complete-kr-checkbox"
              size="lg"
              v-model="event.checkin.newCurrent"
              value="1"
              unchecked-value="0"
              autofocus
              >Complete
            </b-form-checkbox>
          </div>
        </div>
        <div class="kr-value-wrapper" v-else>
          <div class="text-center item">
            <i class="lock-icon mr-1" v-if="isLinkedWithSpreadsheet"></i>
            <label class="mb-1 text-medium-1 label-left-space"
              >Progress ({{ KR_MEASURING_SYMBOL[kr.measureBy] }}):</label
            >
            <div class="d-flex justify-content-center">
              <!-- eslint-disable -->
              <input
                class="form-control text-center current-field"
                :class="{
                  'errored-field': errorMessages.checkin || errorMessages.current,
                  fetching: fetchingCurrent
                }"
                :type="fetchingCurrent ? 'text' : 'number'"
                v-autoselect
                :disabled="isLinkedWithSpreadsheet"
                :value="fetchingCurrent ? 'Fetching...' : event.checkin.newCurrent"
                @input="event.checkin.newCurrent = $event.target.value"
                @keydown.PLUS.MINUS.e.prevent
              />
            </div>
            <div
              class="d-flex align-items-center justify-content-center flex-grow-1 mt-1 text-mini-1"
              v-if="kr.measureBy !== KR_MEASURING_UNIT.BOOLEAN"
            >
              <span>{{ kr.start }}</span>
              <span class="pr-1 pl-1 arrow-next">➝</span>
              <span>{{ kr.end }}</span>
            </div>
          </div>
        </div>
        <div class="checkin-status">
          <div>
            <label class="status-label mb-1 text-medium-1">Current Status:</label>
            <checkin-status-select-buttons
              class="status-select"
              :items="statuses"
              v-model="event.checkin.newStatus"
            />
          </div>
        </div>
      </div>
      <template v-if="getCheckinErrors">
        <div class="field-error-message" v-for="error in getCheckinErrors()" :key="error">
          {{ error }}
        </div>
      </template>
      <div class="item mt-3">
        <div class="d-flex align-items-center pl-3">
          <label class="text-medium-1 mb-0">Check-in note</label>
          <div class="d-flex align-items-center pl-2">
            <b-button
              @click="showCheckinTemplatesModal"
              variant="link"
              class="text-secondary d-flex align-items-center text-medium-1"
            ><i class="bulb-icon mr-1"></i>See Examples
            </b-button>
          </div>
        </div>
        <div class="editor-wrapper" :class="{ 'errored-field': errorMessages.comment }">
          <ck-editor
            v-if="shouldRenderEditor"
            ref="checkinCommentEditor"
            :config="ckeditorConfig"
            :id="editorId"
            @change="autoSaveComment"
            @save="saveCheckin($event)"
            :initial-data="isEdit ? comment : defaultContent"
            :initial-data-type="commentType || null"
          />
        </div>
      </div>
      <template v-if="errorMessages.comment">
        <div class="field-error-message" v-for="error in errorMessages.comment" :key="error">
          {{ error }}
        </div>
      </template>
      <submit-buttons
        :submit-text="eventId ? 'Update' : 'Check-In'"
        class="mt-3 text-nowrap"
        :showConfettis="true"
        :animateConfettis="animateConfettis"
        :show-notify-subscribers="isEdit"
        :is-loading="postingCheckin"
        @save="saveCheckin($refs.checkinCommentEditor.getData(), $event)"
        @cancel="onCancel"
      >
        <subscribers :org-id="orgId" :entity-type="ENTITY_TYPE.KEY_RESULT" :entity="kr" />
      </submit-buttons>
      <checkin-templates
        v-model="showCheckinTemplate"
        @select-template="onTemplateSelection"
        :warn-on-selection="!!$refs.checkinCommentEditor.getData()"
        v-if="showCheckinTemplate"
      />
    </div>
  </div>
</template>

<script>
import { getCKEditorConfig } from '@/ckeditorConfig'
import CheckinStatusSelectButtons from '@/components/CheckinStatusSelectButtons'
import CkEditor from '@/components/CkEditor'
import CheckinTemplates from '@/components/kr/CheckinTemplates'
import SubmitButtons from '@/components/SubmitButtons'
import Subscribers from '@/components/Subscribers'
import { EVENT_ACTION, EVENT_CATEGORY, EVENT_NAME } from '@/constants/analytics'
import { MIME_TYPE } from '@/constants/common'
import { ENTITY_TYPE } from '@/constants/entities'
import events from '@/constants/events'
import { featureFlags } from '@/constants/featureFlags'
import { KEY_CODES } from '@/constants/keyCodes'
import { EXT_LOOKUP, KR_MEASURING_SYMBOL, KR_MEASURING_UNIT, KR_STATUS } from '@/constants/kr'
import EventBus from '@/EventBus'
import { formatHyphenatedString } from '@/goalUtils'
import { checkinCommentStorageManager } from '@/helpers/autoSaveManager'
import ComponentIdMixin from '@/mixins/ComponentIdMixin'
import EventBusMixin from '@/mixins/EventBusMixin'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import GoogleSpreadsheetMixin from '@/mixins/GoogleSpreadsheetMixin'
import MembershipsMixin from '@/mixins/MembershipsMixin'
import ParentGoalsMixin from '@/mixins/ParentGoalsMixin'
import service from '@/service'
import urlManager from '@/urlManager'
import getCkEditorPlainText from '@/utils/ckeditor/getCkEditorPlainText'
import { isCtrlEvent } from '@/utils/kbEvents'
import showConfirm from '@/utils/showConfirm'
import showToast from '@/utils/showToast'
import { BButton, BFormCheckbox } from 'bootstrap-vue'
import * as R from 'ramda'
import { mapActions } from 'vuex'
import { trackEvent } from '../../analytics'

function prepareCheckinEvent(kr, eventId) {
  return {
    eventId: eventId,
    commentType: MIME_TYPE.HTML,
    checkin: {
      keyResultId: kr.id,
      oldStatus: kr.status,
      newStatus: kr.status,
      oldCurrent: kr.current,
      newCurrent: kr.current
    }
  }
}

export default {
  name: 'CheckinCard',
  props: ['kr', 'orgId', 'isCollapsed', 'eventId', 'comment', 'commentType'],
  components: {
    CheckinStatusSelectButtons,
    BFormCheckbox,
    SubmitButtons,
    CkEditor,
    Subscribers,
    BButton,
    CheckinTemplates
  },
  mixins: [
    GoogleSpreadsheetMixin,
    MembershipsMixin,
    EventBusMixin,
    ComponentIdMixin,
    ParentGoalsMixin,
    FeaturesMixin
  ],
  data() {
    const autoSaveKey = `kr-${this.kr.id}`
    return {
      statuses: Object.values(KR_STATUS).map(status => ({
        value: status,
        label: this.krStatusText(status),
        type: status
      })),
      sheetInfo: {
        spreadsheetUrl: null,
        spreadsheetTitle: null,
        sheetTitle: null,
        cell: null
      },
      event: prepareCheckinEvent(this.kr, this.eventId),
      KR_MEASURING_SYMBOL,
      KR_MEASURING_UNIT,
      errorMessages: {},
      fetchingCurrent: false,
      animateConfettis: false,
      editorId: 'checkinCommentEditor' + this.uuid,
      postingCheckin: false,
      isCheckinCollapsed: this.isCollapsed,
      ENTITY_TYPE,
      showCheckinTemplate: false,
      autoSaveKey,
      savedContent: checkinCommentStorageManager.getSavedContent(autoSaveKey),
      checkinTemplate: null,
      isOrgBasedCheckinTemplateAllowed: this.getFeatureFlagValue(
        this.orgId,
        featureFlags.ALLOW_ORG_BASED_CHECKIN_TEMPLATE
      )
    }
  },
  computed: {
    defaultContent() {
      if (this.savedContent) {
        return this.savedContent
      } else if (this.isOrgBasedCheckinTemplateAllowed) {
        return this.checkinTemplate
      }
      return undefined
    },
    shouldRenderEditor() {
      const isUsersAvailable = this.usersInScope.length > 0
      if (this.isEdit) {
        return isUsersAvailable
      } else {
        // if there is no saved content, render after checkin template is fetched
        return (
          isUsersAvailable &&
          (this.savedContent ||
            !this.isOrgBasedCheckinTemplateAllowed ||
            this.checkinTemplate !== null)
        )
      }
    },
    isEdit() {
      return !!this.eventId
    },
    isLinkedWithSpreadsheet() {
      return this.kr.extLookup === EXT_LOOKUP.GOOGLE_SPREADSHEET
    },
    goal() {
      return this.$store.getters.goalById(this.kr.goalId)
    },
    usersInScope() {
      return this.getUsersInScope(this.goal.teamId, this.goal.organizationId)
    },
    ckeditorConfig() {
      return getCKEditorConfig({
        height: 130,
        users: this.usersInScope,
        orgId: this.orgId,
        placeholder: '+ Summary of check-in'
      })
    }
  },
  methods: {
    ...mapActions(['createEvent', 'updateGoalEvent']),
    krStatusText: formatHyphenatedString,
    onCheckinSuccess(event) {
      showToast('Checkin has been posted successfully')
      this.$emit('post-checkin', event)
      EventBus.$emit(events.CHECKIN_POSTED, event)
      this.refreshParentGoals(this.goal.id) // Did this to make sure that UI is updated with progress of all parent goals
      this.clearEditorStoredData()
      this.isCheckinCollapsed = true
    },
    onCheckinUpdateSuccess(event) {
      showToast('Checkin has been updated successfully')
      this.$emit('update-checkin', { checkin: event.checkin, comment: event.comment })
      this.clearEditorStoredData()
      this.isCheckinCollapsed = true
    },
    autoSaveComment(data) {
      !this.isEdit && checkinCommentStorageManager.saveContent(this.autoSaveKey, data)
    },
    async saveCheckin(comment, notifySubscribers) {
      if (!comment) {
        comment = this.$refs.checkinCommentEditor.getData()
      }
      this.errorMessages = []
      this.postingCheckin = true
      if (this.eventId) {
        this.updateCheckin(this.event, comment, notifySubscribers)
      } else {
        try {
          let response = await this.createEvent({
            orgId: this.kr.organizationId,
            goalId: this.kr.goalId,
            event: { ...this.event, comment }
          })
          this.event = prepareCheckinEvent(this.kr)
          if (this.event.checkin.newStatus === KR_STATUS.ON_TRACK) {
            this.animateConfettis = true
            // wait for confettis animation to get over
            setTimeout(() => {
              this.animateConfettis = false
              this.onCheckinSuccess(response)
            }, 750)
          } else {
            this.onCheckinSuccess(response)
          }
        } catch (e) {
          const response = R.pathOr({}, ['response', 'data'], e)
          if (response.detail && !R.isEmpty(response.detail)) {
            this.errorMessages = response.detail
          } else {
            this.errorMessages = { errors: [response.message] }
          }
        }
      }
      this.postingCheckin = false
    },
    async updateCheckin(event, comment, notifySubscribers) {
      const payload = {
        comment: comment,
        commentType: MIME_TYPE.HTML,
        goalId: this.kr.goalId,
        orgId: this.kr.organizationId,
        eventId: this.eventId,
        krId: this.kr.id,
        checkin: event.checkin,
        notifySubscribers
      }
      const response = await this.updateGoalEvent(payload)
      if (this.event.checkin.newStatus === KR_STATUS.ON_TRACK) {
        this.animateConfettis = true
        // wait for confettis animation to get over
        setTimeout(() => {
          this.animateConfettis = false
          this.onCheckinUpdateSuccess(response)
        }, 750)
      } else {
        this.onCheckinUpdateSuccess(response)
      }
    },
    getCheckinErrors() {
      if (this.errorMessages.checkin) {
        return R.flatten(Object.values(this.errorMessages.checkin))
      } else if (this.errorMessages.current) {
        return this.errorMessages.current
      }
    },
    onCardKeyDown(event) {
      if (event.keyCode === KEY_CODES.ENTER && !isCtrlEvent(event)) {
        event.stopPropagation()
      }
    },
    clearEditorStoredData() {
      !this.isEdit && checkinCommentStorageManager.clearSavedContent(this.autoSaveKey)
      this.savedContent = null
    },
    triggerCancellation() {
      this.$emit('close')
      this.isCheckinCollapsed = true
      this.clearEditorStoredData()
    },
    onCancel() {
      if (getCkEditorPlainText(this.$refs.checkinCommentEditor)) {
        showConfirm({
          body: 'This action will discard the unsaved changes. Are you sure you want to proceed?',
          callback: this.triggerCancellation,
          variant: 'warning'
        })
      } else {
        this.triggerCancellation()
      }
    },
    onTemplateSelection(template) {
      this.$refs.checkinCommentEditor.setData(template)
    },
    showCheckinTemplatesModal() {
      this.showCheckinTemplate = true
      trackEvent(EVENT_CATEGORY.HELP, EVENT_ACTION.CHECKIN_TEMPLATE_SEEN, EVENT_NAME.TOP)
    },
    async fetchCheckinTemplate() {
      try {
        const response = await service.client.get(urlManager.checkinTemplate(this.orgId))
        this.checkinTemplate = response.data.checkinTemplate || ''
      } catch (e) {
        this.checkinTemplate = ''
        // ignore
        // it gives 404 when no rule is set
      }
    }
  },
  async mounted() {
    if (this.isLinkedWithSpreadsheet) {
      this.fetchingCurrent = true
      const {
        value,
        spreadsheetUrl,
        spreadsheetTitle,
        sheetTitle,
        cell,
        errors
      } = await this.getCurrentValue(this.kr)
      this.fetchingCurrent = false
      if (errors) {
        this.errorMessages = { ...this.errorMessages, current: errors }
      } else {
        this.event.checkin.newCurrent = value
        this.sheetInfo.spreadsheetUrl = spreadsheetUrl
        this.sheetInfo.spreadsheetTitle = spreadsheetTitle
        this.sheetInfo.sheetTitle = sheetTitle
        this.sheetInfo.cell = cell
      }
    }
    this.fetchUsersInScope(this.goal)
    this.listenToEventBus('SUBMIT_FORM', this.saveCheckin)
    if (this.isOrgBasedCheckinTemplateAllowed) this.fetchCheckinTemplate()
  },
  watch: {
    kr(kr) {
      this.event = prepareCheckinEvent(kr, this.eventId)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/styleConfigurations';
@import '~@/styles/goals';
@import '~@/styles/errors';
@import '~@/styles/components/input';

.check-in-card-wrapper {
  position: relative;

  .bool-handler {
    label .status {
      font-weight: 600;
    }

    .complete-kr-checkbox {
      font-weight: 600;
      font-size: 15px;
      margin-top: 4px;
    }
  }

  .arrow-next {
    color: #8a8a8a;
  }

  .checkin-status {
    padding: 0 40px;
  }

  input {
    font-size: 1rem;
    width: 150px;
    padding: 13px 26px;
    font-weight: 500;
    border-color: $tertiary;
  }
}

input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  -moz-appearance: textfield;
  padding: 0;
}

input.fetching {
  font-size: 12px;
  font-weight: normal;
}

.current-field {
  min-height: 38px;
  border-radius: 5px;
}

input[disabled] {
  cursor: not-allowed;
}

.google-sheet-icon {
  display: inline-block;
  height: 22px;
  width: 25px;
  background: url(~@/assets/google-sheet.svg) no-repeat;
}

.editor-wrapper {
  border: 1px solid $tertiary;
  padding-left: 10px;
  border-radius: 10px;
  overflow: hidden;
  min-height: 184px;
  padding-bottom: 5px;
  background-color: $documentColor;
}

.status-select {
  padding: 4px 0;
}

/deep/ .status-select .widget-item {
  padding: 4px 10px;
}

.templates-link {
  &,
  &:hover {
    color: $secondary;
  }
}

/deep/ .cke_bottom {
  right: 3px;
  bottom: -3px;
}

.placeholder {
  cursor: text;
}

@include media-breakpoint-down(md) {
  .checkin-value-wrapper {
    flex-direction: column;
  }
  .status-label {
    display: none;
  }
  .checkin-status {
    margin: 20px 0 12px 0;
  }
  .bool-handler {
    text-align: center;
  }
}
</style>
<style>
.checkin-alert button.close {
  font-size: 1rem;
}
</style>
